/**
 * WordPress import and source probing APIs
 */

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { API_BASE, apiFetch, parseApiResponse, throwResponseError } from "./client.js";

// =============================================================================
// WordPress Import API
// =============================================================================

/** Field compatibility status */
export type FieldCompatibility =
	| "compatible" // Field exists with compatible type
	| "type_mismatch" // Field exists but type differs
	| "missing"; // Field doesn't exist

/** Single field definition for import */
export interface ImportFieldDef {
	slug: string;
	label: string;
	type: string;
	required: boolean;
}

/** Schema status for a collection */
export interface CollectionSchemaStatus {
	exists: boolean;
	fieldStatus: Record<
		string,
		{
			status: FieldCompatibility;
			existingType?: string;
			requiredType: string;
		}
	>;
	canImport: boolean;
	reason?: string;
}

/** Post type with full schema info */
export interface PostTypeAnalysis {
	name: string;
	count: number;
	suggestedCollection: string;
	requiredFields: ImportFieldDef[];
	schemaStatus: CollectionSchemaStatus;
}

/** Individual attachment info for media import */
export interface AttachmentInfo {
	id?: number;
	title?: string;
	url?: string;
	filename?: string;
	mimeType?: string;
}

/** Navigation menu from WordPress */
export interface NavMenu {
	name: string;
	slug: string;
	count: number;
}

/** Custom taxonomy from WordPress */
export interface CustomTaxonomy {
	name: string;
	slug: string;
	count: number;
	hierarchical: boolean;
}

/** Author info from WordPress */
export interface WpAuthorInfo {
	id?: number;
	login?: string;
	email?: string;
	displayName?: string;
	postCount: number;
}

export interface WxrAnalysis {
	site: {
		title: string;
		url: string;
	};
	postTypes: PostTypeAnalysis[];
	attachments: {
		count: number;
		items: AttachmentInfo[];
	};
	categories: number;
	tags: number;
	authors: WpAuthorInfo[];
	customFields: Array<{
		key: string;
		count: number;
		samples: string[];
		suggestedField: string;
		suggestedType: string;
		isInternal: boolean;
	}>;
	/** Navigation menus found in the export */
	navMenus?: NavMenu[];
	/** Custom taxonomies found in the export */
	customTaxonomies?: CustomTaxonomy[];
}

export interface PrepareRequest {
	postTypes: Array<{
		name: string;
		collection: string;
		fields: ImportFieldDef[];
	}>;
}

export interface PrepareResult {
	success: boolean;
	collectionsCreated: string[];
	fieldsCreated: Array<{ collection: string; field: string }>;
	errors: Array<{ collection: string; error: string }>;
}

/** Author mapping from WP author login to EmDash user ID */
export interface AuthorMapping {
	/** WordPress author login */
	wpLogin: string;
	/** WordPress author display name (for UI) */
	wpDisplayName: string;
	/** WordPress author email (for matching) */
	wpEmail?: string;
	/** EmDash user ID to assign (null = leave unassigned) */
	emdashUserId: string | null;
	/** Number of posts by this author */
	postCount: number;
}

export interface ImportConfig {
	postTypeMappings: Record<
		string,
		{
			collection: string;
			enabled: boolean;
		}
	>;
	skipExisting: boolean;
	/** Author mappings (WP author login -> EmDash user ID) */
	authorMappings?: Record<string, string | null>;
	/** Import navigation menus (plugin import; default true) */
	importMenus?: boolean;
	/** Take over site title & tagline (plugin import; default true) */
	importSiteTitle?: boolean;
	/** Take over logo & favicon (plugin import; default true) */
	importLogo?: boolean;
	/** Import per-post Yoast/Rank Math SEO fields (plugin import; default true) */
	importSeo?: boolean;
}

export interface ImportResult {
	success: boolean;
	imported: number;
	skipped: number;
	errors: Array<{ title: string; error: string }>;
	byCollection: Record<string, number>;
	/** Number of taxonomy term assignments written (plugin import) */
	taxonomyAssignments?: number;
	/** Source taxonomies skipped because no matching EmDash taxonomy def exists */
	missingTaxonomies?: string[];
	/** Custom taxonomy defs auto-created during the import (plugin import) */
	taxonomiesCreated?: string[];
	/** Navigation menu import summary (plugin import) */
	menus?: { created: number; items: number };
	/** Comment import summary (plugin import) */
	comments?: { imported: number; skipped: number };
	/** Site settings applied from the source (plugin import) */
	siteSettings?: string[];
}

// =============================================================================
// Chunked plugin import (issue #475)
//
// A single /execute request importing a whole site exceeds Cloudflare
// Worker resource limits. The admin drives the import as a loop of bounded
// requests instead: one WP content page per call, then paginated comments,
// then a small finalize step. Cross-chunk state (ID maps, translation
// groups, comment roots) is accumulated here and sent with each request,
// so the server stays stateless and an aborted import can simply re-run.
// =============================================================================

interface WpImportCursor {
	postTypeIndex: number;
	page: number;
}

/** Client-accumulated state passed back to the server with each chunk. */
interface WpImportChunkState {
	idMap: Record<string, { id: string; collection: string }>;
	translationGroups: Record<string, string>;
	commentRoots: Record<string, string>;
}

interface WpImportChunkResponse {
	success: boolean;
	result: ImportResult;
	done: boolean;
	cursor?: WpImportCursor;
	chunk?: Partial<WpImportChunkState>;
}

/** Progress snapshot reported after every chunk. */
export interface WpImportProgress {
	phase: "content" | "comments" | "finalize";
	/** Content items imported + skipped so far (content phase) */
	processed: number;
	/** Comments imported + skipped so far (comments phase) */
	comments: number;
}

async function executeWpPluginImportChunk(
	url: string,
	token: string,
	config: ImportConfig,
	phase: WpImportProgress["phase"],
	cursor: WpImportCursor | undefined,
	state: WpImportChunkState,
): Promise<WpImportChunkResponse> {
	const response = await apiFetch(`${API_BASE}/import/wordpress-plugin/execute`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			url,
			token,
			config,
			phase,
			cursor,
			idMap: state.idMap,
			translationGroups: state.translationGroups,
			commentRoots: state.commentRoots,
		}),
	});
	return parseApiResponse<WpImportChunkResponse>(response, "Failed to import from WordPress");
}

/** Merge a chunk's partial result into the running aggregate. */
function mergeImportResults(into: ImportResult, chunk: ImportResult): void {
	into.imported += chunk.imported;
	into.skipped += chunk.skipped;
	into.errors.push(...chunk.errors);
	for (const [collection, count] of Object.entries(chunk.byCollection)) {
		into.byCollection[collection] = (into.byCollection[collection] || 0) + count;
	}
	if (chunk.taxonomyAssignments) {
		into.taxonomyAssignments = (into.taxonomyAssignments ?? 0) + chunk.taxonomyAssignments;
	}
	if (chunk.missingTaxonomies?.length) {
		into.missingTaxonomies = [
			...new Set([...(into.missingTaxonomies ?? []), ...chunk.missingTaxonomies]),
		];
	}
	if (chunk.taxonomiesCreated?.length) {
		into.taxonomiesCreated = [...(into.taxonomiesCreated ?? []), ...chunk.taxonomiesCreated];
	}
	if (chunk.menus) into.menus = chunk.menus;
	if (chunk.comments) {
		into.comments = {
			imported: (into.comments?.imported ?? 0) + chunk.comments.imported,
			skipped: (into.comments?.skipped ?? 0) + chunk.comments.skipped,
		};
	}
	if (chunk.siteSettings) into.siteSettings = chunk.siteSettings;
	into.success = into.errors.length === 0;
}

/**
 * Run the full plugin import as a sequence of bounded requests: content
 * pages, then comment pages, then finalize (menus + site identity).
 * Each request stays well below Worker resource limits regardless of
 * site size. Re-running after an abort is safe: `skipExisting` skips
 * already-imported content while rebuilding the ID maps the later
 * phases need.
 */
export async function executeWpPluginImport(
	url: string,
	token: string,
	config: ImportConfig,
	onProgress?: (progress: WpImportProgress) => void,
): Promise<ImportResult> {
	const aggregate: ImportResult = {
		success: true,
		imported: 0,
		skipped: 0,
		errors: [],
		byCollection: {},
	};
	const state: WpImportChunkState = { idMap: {}, translationGroups: {}, commentRoots: {} };
	let comments = 0;

	const runPhase = async (phase: WpImportProgress["phase"]) => {
		let cursor: WpImportCursor | undefined;
		let done = false;
		while (!done) {
			const chunk = await executeWpPluginImportChunk(url, token, config, phase, cursor, state);
			mergeImportResults(aggregate, chunk.result);
			Object.assign(state.idMap, chunk.chunk?.idMap);
			Object.assign(state.translationGroups, chunk.chunk?.translationGroups);
			Object.assign(state.commentRoots, chunk.chunk?.commentRoots);
			comments += (chunk.result.comments?.imported ?? 0) + (chunk.result.comments?.skipped ?? 0);
			onProgress?.({
				phase,
				processed: aggregate.imported + aggregate.skipped,
				comments,
			});
			done = chunk.done;
			cursor = chunk.cursor;
		}
	};

	await runPhase("content");
	await runPhase("comments");
	await runPhase("finalize");

	return aggregate;
}

/**
 * Analyze a WordPress WXR file
 */
export async function analyzeWxr(file: File): Promise<WxrAnalysis> {
	const formData = new FormData();
	formData.append("file", file);

	const response = await apiFetch(`${API_BASE}/import/wordpress/analyze`, {
		method: "POST",
		body: formData,
	});
	return parseApiResponse<WxrAnalysis>(response, "Failed to analyze file");
}

/**
 * Prepare WordPress import (create collections/fields)
 */
export async function prepareWxrImport(request: PrepareRequest): Promise<PrepareResult> {
	const response = await apiFetch(`${API_BASE}/import/wordpress/prepare`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(request),
	});
	return parseApiResponse<PrepareResult>(response, "Failed to prepare import");
}

/**
 * Execute WordPress import
 */
export async function executeWxrImport(file: File, config: ImportConfig): Promise<ImportResult> {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("config", JSON.stringify(config));

	const response = await apiFetch(`${API_BASE}/import/wordpress/execute`, {
		method: "POST",
		body: formData,
	});
	return parseApiResponse<ImportResult>(response, "Failed to import");
}

// =============================================================================
// Media Import API
// =============================================================================

export interface MediaImportResult {
	imported: Array<{
		wpId?: number;
		originalUrl: string;
		newUrl: string;
		mediaId: string;
	}>;
	failed: Array<{
		wpId?: number;
		originalUrl: string;
		error: string;
	}>;
	urlMap: Record<string, string>;
}

/** Progress update sent during streaming media import */
export interface MediaImportProgress {
	type: "progress";
	current: number;
	total: number;
	filename?: string;
	status: "downloading" | "uploading" | "done" | "skipped" | "failed";
	error?: string;
}

export interface RewriteUrlsResult {
	updated: number;
	byCollection: Record<string, number>;
	urlsRewritten: number;
	errors: Array<{ collection: string; id: string; error: string }>;
}

/**
 * Import media from WordPress with streaming progress
 *
 * @param attachments - Array of attachments to import
 * @param onProgress - Callback for progress updates (optional)
 * @returns Final import result
 */
export async function importWxrMedia(
	attachments: AttachmentInfo[],
	onProgress?: (progress: MediaImportProgress) => void,
): Promise<MediaImportResult> {
	const response = await apiFetch(`${API_BASE}/import/wordpress/media`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ attachments, stream: !!onProgress }),
	});

	if (!response.ok) await throwResponseError(response, i18n._(msg`Failed to import media`));

	// If no progress callback, just parse as JSON (non-streaming mode)
	// Note: streaming NDJSON responses are excluded from the { success, data } envelope
	if (!onProgress) {
		return parseApiResponse<MediaImportResult>(response, "Failed to import media");
	}

	// Streaming mode: read NDJSON line by line
	const reader = response.body?.getReader();
	if (!reader) {
		throw new Error("Response body is not readable");
	}

	const decoder = new TextDecoder();
	let buffer = "";
	let result: MediaImportResult | null = null;

	while (true) {
		const { done, value } = await reader.read();

		if (done) break;

		buffer += decoder.decode(value, { stream: true });

		// Process complete lines
		const lines = buffer.split("\n");
		buffer = lines.pop() || ""; // Keep incomplete line in buffer

		for (const line of lines) {
			if (!line.trim()) continue;

			try {
				const parsed: { type?: string; imported?: unknown } = JSON.parse(line);
				if (parsed.type === "progress") {
					// eslint-disable-next-line typescript/no-unsafe-type-assertion -- SSE event data is parsed JSON; discriminated by type === "progress"
					onProgress(parsed as MediaImportProgress);
				} else if (parsed.type === "result" || parsed.imported) {
					// Final result (has type: "result" or is the result object)
					// eslint-disable-next-line typescript/no-unsafe-type-assertion -- SSE event data is parsed JSON; discriminated by type === "result"
					result = parsed as MediaImportResult;
				}
			} catch {
				// Ignore parse errors for incomplete JSON
				console.warn("Failed to parse NDJSON line:", line);
			}
		}
	}

	// Process any remaining data in buffer
	if (buffer.trim()) {
		try {
			const parsed: { type?: string; imported?: unknown } = JSON.parse(buffer);
			if (parsed.type === "result" || parsed.imported) {
				// eslint-disable-next-line typescript/no-unsafe-type-assertion -- SSE event data is parsed JSON; discriminated by type === "result"
				result = parsed as MediaImportResult;
			}
		} catch {
			console.warn("Failed to parse final NDJSON:", buffer);
		}
	}

	if (!result) {
		throw new Error("No result received from media import");
	}

	return result;
}

/** Attachments per media request. Bounds each Worker invocation (issue #475). */
const MEDIA_BATCH_SIZE = 25;

/**
 * Import media in bounded batches instead of one giant request, so each
 * Worker invocation stays below resource limits and an aborted run only
 * loses the batch in flight (the server dedupes re-sent files by content
 * hash). Progress is reported against the overall total.
 */
export async function importWxrMediaBatched(
	attachments: AttachmentInfo[],
	onProgress?: (progress: MediaImportProgress) => void,
): Promise<MediaImportResult> {
	const merged: MediaImportResult = { imported: [], failed: [], urlMap: {} };

	for (let offset = 0; offset < attachments.length; offset += MEDIA_BATCH_SIZE) {
		const batch = attachments.slice(offset, offset + MEDIA_BATCH_SIZE);
		const result = await importWxrMedia(
			batch,
			onProgress &&
				((progress) =>
					onProgress({
						...progress,
						current: offset + progress.current,
						total: attachments.length,
					})),
		);
		merged.imported.push(...result.imported);
		merged.failed.push(...result.failed);
		Object.assign(merged.urlMap, result.urlMap);
	}

	return merged;
}

// =============================================================================
// Import Source Probing
// =============================================================================

/** Capabilities of an import source */
export interface SourceCapabilities {
	publicContent: boolean;
	privateContent: boolean;
	customPostTypes: boolean;
	allMeta: boolean;
	mediaStream: boolean;
}

/** Auth requirements for import */
export interface SourceAuth {
	type: "oauth" | "token" | "password" | "none";
	provider?: string;
	oauthUrl?: string;
	instructions?: string;
}

/** Suggested action after probing */
export type SuggestedAction =
	| { type: "proceed" }
	| { type: "oauth"; url: string; provider: string }
	| { type: "upload"; instructions: string }
	| { type: "install-plugin"; instructions: string };

/** Result from probing a single source */
export interface SourceProbeResult {
	sourceId: string;
	confidence: "definite" | "likely" | "possible";
	detected: {
		platform: string;
		version?: string;
		siteTitle?: string;
		siteUrl?: string;
	};
	capabilities: SourceCapabilities;
	auth?: SourceAuth;
	suggestedAction: SuggestedAction;
	preview?: {
		posts?: number;
		pages?: number;
		media?: number;
	};
}

/** Combined probe result */
export interface ProbeResult {
	url: string;
	isWordPress: boolean;
	bestMatch: SourceProbeResult | null;
	allMatches: SourceProbeResult[];
}

/**
 * Probe a URL to detect import source
 */
export async function probeImportUrl(url: string): Promise<ProbeResult> {
	const response = await apiFetch(`${API_BASE}/import/probe`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ url }),
	});
	const data = await parseApiResponse<{ result: ProbeResult }>(response, "Failed to probe URL");
	return data.result;
}

/**
 * Rewrite URLs in content after media import
 */
export async function rewriteContentUrls(
	urlMap: Record<string, string>,
	collections?: string[],
): Promise<RewriteUrlsResult> {
	const response = await apiFetch(`${API_BASE}/import/wordpress/rewrite-urls`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ urlMap, collections }),
	});
	return parseApiResponse<RewriteUrlsResult>(response, "Failed to rewrite URLs");
}

// =============================================================================
// WordPress Plugin Direct Import API
// =============================================================================

/** WordPress Plugin analysis result */
export interface WpPluginAnalysis {
	sourceId: string;
	site: {
		title: string;
		url: string;
	};
	postTypes: PostTypeAnalysis[];
	attachments: {
		count: number;
		items: AttachmentInfo[];
	};
	categories: number;
	tags: number;
	authors: WpAuthorInfo[];
	/** Navigation menus found via the plugin */
	navMenus?: NavMenu[];
	/** Custom taxonomies found via the plugin */
	customTaxonomies?: CustomTaxonomy[];
}

/**
 * Analyze a WordPress site with EmDash Exporter plugin
 */
export async function analyzeWpPluginSite(url: string, token: string): Promise<WpPluginAnalysis> {
	const response = await apiFetch(`${API_BASE}/import/wordpress-plugin/analyze`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ url, token }),
	});
	const data = await parseApiResponse<{ analysis: WpPluginAnalysis }>(
		response,
		"Failed to analyze WordPress site",
	);
	return data.analysis;
}
